'use client';

import { memo, useRef, useState, useCallback, useMemo, useEffect, type DragEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { Icon } from '@iconify/react';
import { isAcceptedAttachment, isAcceptedImage } from '@/features/bug-report/utils/file-acceptance';

const MAX_IMAGES = 4;

interface ImageGalleryProps {
  screenshot: File | null;
  additionalImages: File[];
  attachedFiles: File[];
  onAddImages: (files: FileList | null) => void;
  onAddFiles: (files: FileList | null) => void;
  onAddCapture: () => void;
  onRemoveImage: (index: number) => void;
  onRemoveFile: (index: number) => void;
  disabled?: boolean;
}

function ImageGalleryImpl({
  screenshot,
  additionalImages,
  attachedFiles,
  onAddImages,
  onAddFiles,
  onAddCapture,
  onRemoveImage,
  onRemoveFile,
  disabled,
}: ImageGalleryProps) {
  /** Browse / Attach File / drag-drop: no `accept` — classify client-side (avoids mixed MIME+extensions on Windows). */
  const combinedZoneInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const allImages = useMemo(() => {
    const items: Array<{ file: File; removable: boolean; label?: string }> = [];
    if (screenshot) {
      items.push({ file: screenshot, removable: false, label: 'Auto-capture' });
    }
    additionalImages.forEach((file) => {
      items.push({ file, removable: true });
    });
    return items;
  }, [screenshot, additionalImages]);

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  useEffect(() => {
    const urls = allImages.map((item) => URL.createObjectURL(item.file));
    setImageUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [allImages]);

  const routePickedFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList?.length) return;
      const dropped = Array.from(fileList);
      const images = dropped.filter(isAcceptedImage);
      const files = dropped.filter((f) => !isAcceptedImage(f) && isAcceptedAttachment(f));

      if (images.length > 0) {
        const dt = new DataTransfer();
        images.forEach((f) => dt.items.add(f));
        onAddImages(dt.files);
      }
      if (files.length > 0) {
        const dt = new DataTransfer();
        files.forEach((f) => dt.items.add(f));
        onAddFiles(dt.files);
      }
    },
    [onAddImages, onAddFiles]
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (disabled || !e.dataTransfer.files.length) return;
      routePickedFiles(e.dataTransfer.files);
    },
    [disabled, routePickedFiles]
  );

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        border: dragOver ? '2px dashed' : '2px solid transparent',
        borderColor: dragOver ? 'primary.main' : 'transparent',
        borderRadius: 1,
        bgcolor: dragOver ? 'action.hover' : 'transparent',
        transition: 'all 0.2s',
        p: dragOver ? 1 : 0,
      }}
    >
      {dragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 1,
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
            Drop files here
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Images ({allImages.length}/{MAX_IMAGES})</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onAddCapture}
            disabled={disabled || allImages.length >= MAX_IMAGES}
            startIcon={<Icon icon="material-symbols:screenshot-monitor" width={16} />}
          >
            Capture Screen
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {allImages.map((item, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              width: 120,
              height: 90,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              component="img"
              src={imageUrls[index]}
              alt={item.label ?? item.file.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {item.label && (
              <Chip
                label={item.label}
                size="small"
                sx={{ position: 'absolute', bottom: 4, left: 4, fontSize: '0.65rem' }}
              />
            )}
            {item.removable && !disabled && (
              <IconButton
                size="small"
                onClick={() => onRemoveImage(index - (screenshot ? 1 : 0))}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  p: 0.25,
                }}
              >
                <Icon icon="material-symbols:close" width={16} height={16} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Attachments ({attachedFiles.length})</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => !disabled && combinedZoneInputRef.current?.click()}
          disabled={disabled}
          startIcon={<Icon icon="material-symbols:attach-file" width={16} />}
        >
          Attach File
        </Button>
      </Box>
      {attachedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {attachedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.75,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Icon icon="material-symbols:description" width={18} />
              <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(0)} KB
              </Typography>
              {!disabled && (
                <IconButton size="small" onClick={() => onRemoveFile(index)} sx={{ p: 0.25 }}>
                  <Icon icon="material-symbols:close" width={16} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label="Browse files"
        sx={{
          mt: 1.5,
          py: 1.5,
          border: '1px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 1,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
          transition: 'all 0.2s',
        }}
        onClick={() => !disabled && combinedZoneInputRef.current?.click()}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            combinedZoneInputRef.current?.click();
          }
        }}
      >
        <input
          ref={combinedZoneInputRef}
          type="file"
          multiple
          hidden
          data-testid="bug-report-file-input"
          onChange={(e) => {
            routePickedFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Icon icon="material-symbols:cloud-upload" width={24} style={{ opacity: 0.5, display: 'block', margin: '0 auto' }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Drag & drop images or files here, or click to browse
        </Typography>
      </Box>
    </Box>
  );
}

export const ImageGallery = memo(ImageGalleryImpl);
